import { useState } from "react";

import { ListFilter } from "lucide-react";

import { useAppForm } from "@/components/Form";

import { GetAllCasesApiV1CasesGetData } from "@api/generated/types.gen";

import { Button } from "@components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@components/ui/popover";

import { cn } from "@utils/ui";

import { STATUS_MAPPING } from "../constants/status";
import { FilterFormData } from "../schema/filters";

type FilterProps = {
  className?: string;
  onFiltersChange?: (filters: GetAllCasesApiV1CasesGetData["query"]) => void;
};

export const Filter = ({ className, onFiltersChange }: FilterProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasActiveFilters, setHasActiveFilters] = useState(false);

  const form = useAppForm({
    defaultValues: {
      template_id: "",
      claim_reference_number: "",
      created_at: "",
      examinee_name: "",
      status: undefined,
    } as FilterFormData,
    onSubmit: async ({ value }) => {
      const formattedCreatedAt = value.created_at;

      const filterValues: GetAllCasesApiV1CasesGetData["query"] = {
        template_id: value.template_id || undefined,
        claim_number: value.claim_reference_number || undefined,
        created_at: formattedCreatedAt || undefined,
        examinee_name: value.examinee_name || undefined,
        status: value.status || undefined,
      };

      onFiltersChange?.(filterValues);

      // Check if any filters are applied
      const activeFilters = Object.values(value).some(
        (val) => val && val.trim() !== ""
      );

      setHasActiveFilters(activeFilters);

      setIsOpen(false);
    },
  });

  const statusOptions = Object.entries(STATUS_MAPPING).map(([value, label]) => ({
    value,
    label,
  }));

  const handleCancel = () => {
    form.reset();
    onFiltersChange?.({});
    setHasActiveFilters(false);
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={cn(
              "gap-2 text-foreground transition-all duration-150",
              hasActiveFilters
                ? "border-primary bg-primary/10 hover:text-primary"
                : "border-dark-200 hover:border-primary",
              className
            )}
          >
            <ListFilter className="size-4" />
            Filter
          </Button>
        </PopoverTrigger>

        <PopoverContent
          className="w-96 space-y-1 rounded-xl border border-border bg-background-white px-4 py-4 shadow-lg"
          align="end"
          side="bottom"
          sideOffset={8}
        >
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Filter</h3>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                form.handleSubmit();
              }}
              className="space-y-4"
            >
              <form.AppField name="claim_reference_number">
                {(field) => (
                  <field.InputField
                    label="Claim Reference Number"
                    placeholder="Enter claim reference number"
                  />
                )}
              </form.AppField>

              <form.AppField name="created_at">
                {(field) => (
                  <field.DateField
                    label="Date"
                    placeholder="Select date"
                    dateFormat="yyyy-MM-dd"
                  />
                )}
              </form.AppField>

              <form.AppField name="examinee_name">
                {(field) => (
                  <field.InputField
                    label="Examinee Name"
                    placeholder="Enter examinee name"
                  />
                )}
              </form.AppField>

              <form.AppField name="status">
                {(field) => (
                  <field.SelectField
                    label="Report Status"
                    placeholder="Select a status"
                    options={statusOptions}
                  />
                )}
              </form.AppField>

              <form.AppForm>
                <div className="flex justify-end gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleCancel}
                  >
                    Cancel
                  </Button>
                  <form.SubmitButton size="sm">Apply Filters</form.SubmitButton>
                </div>
              </form.AppForm>
            </form>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};
